import './InlineNumberCell.css';

import { KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import CellContent from '../CellContent/CellContent';

interface InlineNumberCellProps {
  columnName: string;
  value: unknown;
  formattedValue: string;
  min?: number;
  max?: number;
  onSave: (value: number) => Promise<boolean>;
}

const parseValue = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const InlineNumberCell = ({
  columnName,
  value,
  formattedValue,
  min,
  max,
  onSave,
}: InlineNumberCellProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) return;
    setDraft(value === null || value === undefined ? '' : String(value));
    setInvalid(false);
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing, value]);

  const stopRowClick = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
  };

  const activate = (event: MouseEvent<HTMLButtonElement>): void => {
    stopRowClick(event);
    setDraft(value === null || value === undefined ? '' : String(value));
    setInvalid(false);
    setEditing(true);
  };

  const focusSiblingCell = (offset: -1 | 1): void => {
    const current = cellRef.current;
    const container = current?.closest('.grid-container');
    if (!current || !container) return;
    const cells = Array.from(
      container.querySelectorAll<HTMLDivElement>('[data-inline-edit-column]')
    ).filter((cell) => cell.dataset.inlineEditColumn === columnName);
    const next = cells[cells.indexOf(current) + offset];
    window.setTimeout(() =>
      next?.querySelector<HTMLButtonElement>('button')?.click()
    );
  };

  const handleKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>
  ): Promise<void> => {
    event.stopPropagation();
    if (event.key === 'Escape') {
      setEditing(false);
      return;
    }
    const moveOffset =
      event.key === 'ArrowUp'
        ? -1
        : event.key === 'Enter' || event.key === 'ArrowDown'
          ? 1
          : null;
    if (moveOffset === null || saving) return;

    event.preventDefault();
    const parsed = parseValue(draft);
    if (
      parsed === null ||
      (min !== undefined && parsed < min) ||
      (max !== undefined && parsed > max)
    ) {
      setInvalid(true);
      return;
    }

    setSaving(true);
    const saved = await onSave(parsed);
    setSaving(false);
    if (!saved) return;
    setEditing(false);
    focusSiblingCell(moveOffset);
  };

  return (
    <div
      ref={cellRef}
      className="inline-number-cell"
      data-inline-edit-column={columnName}
      onClick={stopRowClick}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="inline-number-cell__input"
          type="text"
          inputMode="decimal"
          value={draft}
          min={min}
          max={max}
          disabled={saving}
          aria-invalid={invalid}
          onChange={(event) => {
            setDraft(event.target.value);
            setInvalid(false);
          }}
          onKeyDown={handleKeyDown}
          onClick={stopRowClick}
        />
      ) : (
        <button
          type="button"
          className="inline-number-cell__button"
          onClick={activate}
        >
          <CellContent value={formattedValue} />
        </button>
      )}
    </div>
  );
};

export default InlineNumberCell;
